//#region Misc
declare enum DeprecatedIdBrand {}

export type DeprecatedFieldT = DeprecatedIdBrand & any;

export const IsoWeekday = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
} as const;

export type IsoWeekdayT = (typeof IsoWeekday)[keyof typeof IsoWeekday];

//#endregion

/**
 * @see getDayIndexOfTaskAlertRepeatDaysByIsoWeekday
 */
export const WeekdayShort = [
  //
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
] as const;

export type WeekdayShortT = (typeof WeekdayShort)[number];

//#region Positioning
export type PositionT = string | null;

export interface IPositionable {
  position: PositionT;
}
//#endregion

//#region Tasks - enums
export enum TaskDueDateGroup {
  TODAY = "Today",
  TOMORROW = "Tomorrow",
  UPCOMING = "Upcoming",
  SOMEDAY = "Someday",
}

export enum TaskRepeatingMethod {
  TASK_REPEAT_OFF = "TASK_REPEAT_OFF",
  TASK_REPEAT_DAY = "TASK_REPEAT_DAY",
  TASK_REPEAT_WEEK = "TASK_REPEAT_WEEK",
  TASK_REPEAT_MONTH = "TASK_REPEAT_MONTH",
  TASK_REPEAT_YEAR = "TASK_REPEAT_YEAR",
}

export enum TaskAlertRepeatEndType {
  REPEAT_END_NEVER = "REPEAT_END_NEVER",
  REPEAT_END_OCCURRENCES = "REPEAT_END_OCCURRENCES",
  REPEAT_END_ON_DATE = "REPEAT_END_ON_DATE",
}

export enum TaskAlertRepeatMonthType {
  ON_DATE = "ON_DATE",
  ON_DAY = "ON_DAY",
}

export enum TaskAlertType {
  NONE = "NONE",
  OFFSET = "OFFSET",
  CUSTOM_TIME = "CUSTOM_TIME" /* NOT USED ANY MORE */,
  CUSTOM_REPEAT = "CUSTOM_REPEAT" /* NOT USED ANY MORE */,
}

export enum TaskPriority {
  DUMMY = "Dummy",
  LOW = "Low",
  NORMAL = "Normal",
  HIGH = "High",
}

export enum TaskStatus {
  DELETED = "DELETED",
  UNCHECKED = "UNCHECKED",
  CHECKED = "CHECKED",
  DONE = "DONE",
}

export enum TaskRecordSyncCounterKeys {
  CATEGORY_ID = "categoryId",
  DUE_DATE = "dueDate",
  NOTE = "note",
  POSITION = "position",
  PRIORITY = "priority",
  STATUS = "status",
  TITLE = "title",
}
//#endregion

//#region Categories - enums
export enum CategoryActiveGroupMethod {
  CLEAN = "clean",
  DATE = "date",
  PRIORITY = "priority",
}
//#endregion

//#region Spaces
export enum WorkspaceTypeT {
  Work = "WORK",
  Family = "FAMILY",
}

export type WorkspacePermissionPropT =
  | "create_board"
  | "invite_member"
  | "remove_member"
  | "get_payment_info"
  | "manage_subscription"
  | "import_space"
  | "create_invitation_request"
  | "toggle_ai_features"
  | "update_member"
  | "list_boards"
  | "list_private_boards"
  | "join_board"
  | "archive_board"
  | "edit_space_info"
  | "manage_templates"
  | "manage_board_members"
  | "delete_space";

export enum WorkspacePermissionLevel {
  ADMIN = 0,
  MEMBER = 1,
  GUEST = 2,
}
//#endregion

//#region Boards
export enum BoardStatus {
  ACTIVE = 0,
  ARCHIVED = 1,
}

export enum BoardPermissionLevel {
  ADMIN = 0,
  MEMBER = 1,
  VIEWER = 2,
}

export type BoardCustomPermissions = { [P in BoardCustomPermissionPropT]: { [K in BoardPermissionLevel]?: boolean } };

export type BoardCustomPermissionPropT = "archive_card" | "delete_card";

export type BoardPermissionPropT =
  | "edit_board"
  | "archive_board"
  | "add_member_to_board"
  | "add_admin_to_board"
  | "remove_member_from_board"
  | "edit_members"
  | "card_comment"
  | "toggle_private"
  | "toggle_public_view"
  | "access_public_view_url"
  | "view_archive"
  | "create_template"
  | "manage_automations"
  | "view_automations"
  | "archive_card"
  | "delete_card"
  | "manage_custom_fields"
  | "toggle_email_to_board"
  | "track_time"
  | "manage_time_trackers"
  | "manage_forms";

//#endregion

//#region Sections
export enum SectionStatus {
  ACTIVE = 0,
  ARCHIVED = 1,
}
//#endregion

//#region Custom Views
export enum CustomViewStatus {
  ACTIVE = 0,
  DELETED = 1,
}

export enum CustomViewType {
  PEOPLE = "PEOPLE",
  DUE_DATE = "DUE_DATE",
  BOARDS_AND_SECTIONS = "BOARDS_AND_SECTIONS",
  COMPLETED_TASKS = "COMPLETED_TASKS",
  TIME_TRACKING = "TIME_TRACKING",
}

export const CustomViewDuration = {
  DAYS_7: "DAYS_7",
  DAYS_14: "DAYS_14",
  WEEKS_4: "WEEKS_4",

  MONTHS_3: "MONTHS_3",
  MONTHS_6: "MONTHS_6",
  MONTHS_12: "MONTHS_12",

  // Note: Temporarily commented out until Custom Duration is implemented
  // This enum is iterated for rendering dropdown options
  // CUSTOM = "CUSTOM",
} as const;

export type CustomViewTaskGroupingT = {
  period: "week" | "fortnight" | "month" | "quarter";
  predicate: "time" | "person";
};

export type CustomViewTimeTrackerWithoutPredicateT =
  | {
      period: "month-to-date" | "last-month" | "six-months";
    }
  | {
      period: "custom";
      after: string;
      before: string;
    };

export type CustomViewTimeTrackerT = CustomViewTimeTrackerWithoutPredicateT & {
  predicate: "CARD" | "PEOPLE";
};

/**
 * @deprecated kept in codebase for backwards compatibility with existing views
 */
export const CustomViewDurationDeprecated = {
  TOMORROW: "TOMORROW",
} as const;

export type CustomViewDurationT =
  | (typeof CustomViewDuration)[keyof typeof CustomViewDuration]
  | (typeof CustomViewDurationDeprecated)[keyof typeof CustomViewDurationDeprecated];

export const CUSTOM_VIEW_DAY_DURATIONS = {
  [CustomViewDuration.DAYS_7]: 7,
  [CustomViewDuration.DAYS_14]: 14,

  // deprecated
  [CustomViewDurationDeprecated.TOMORROW]: 2,
};

export const CUSTOM_VIEW_MONTH_DURATIONS = {
  [CustomViewDuration.MONTHS_3]: 3,
  [CustomViewDuration.MONTHS_6]: 6,
  [CustomViewDuration.MONTHS_12]: 12,
};

export interface ICustomViewCustomDuration {
  from: string;
  to: string;
}
//#endregion

//#region Cards
export enum CardStatus {
  ACTIVE = 0,
  ARCHIVED = 1,
  DELETED = 2,
}
//#endregion

//#region TeamsTag
export enum TeamsTagStatus {
  ACTIVE = 0,
  ARCHIVED = 1,
}
//#endregion

//#region Card Checklist and Attachments
export enum CardCheckListStatus {
  ACTIVE = 0,
  DELETED = 1,
}

export enum CardCheckListItemStatus {
  ACTIVE = 0,
  CHECKED = 1,
  DELETED = 2,
}

export enum CardAttachmentStatus {
  ACTIVE = 0,
  DELETED = 1,
}
//#endregion

//#region MyDay
export enum MyDayEntryStatus {
  UNCHECKED,
  CHECKED,
}

export enum MyDayEntryVisibilityStatus {
  DEFAULT,
  PINNED,
  DISMISSED, // when the user actively dismisses an entry
  AUTO_DISMISSED, // when the client auto-dismisses an entry
}

export enum MyDayReferencedObjectType {
  ANYDO_TASK = 0,
  ANYDO_CARD = 1,
  EXTERNAL_CARD = 2,
}

//#endregion

export interface IDueDateInfo {
  date: number;
  hasTime: boolean;
}

export enum TeamType {
  MARKETING = "MARKETING",
  SALES = "SALES",
  SOFTWARE_DEVELOPMENT = "SOFTWARE_DEVELOPMENT",
  HR = "HR",
  OPERATIONS = "OPERATIONS",
  FINANCE = "FINANCE",
  DESIGN = "DESIGN",
  FREELANCE = "FREELANCE",
  CONTENT = "CONTENT",
  EDUCATION = "EDUCATION",
  LEGAL = "LEGAL",
  IT = "IT",
  OTHER = "OTHER",
}

export type UserReminderConstraint = "fullDay" | "hasTime";

export type UserReminder =
  | {
      id: string;
      type: "preset";
      value: UserReminderPreset;
    }
  | {
      id: string;
      type: "customRelative";
      value: `${number}_${UserReminderUnit}`;
    }
  | {
      id: string;
      type: "presetFromStartDate";
      value: UserReminderPreset;
    }
  | {
      id: string;
      type: "customRelativeFromStartDate";
      value: `${number}_${UserReminderUnit}`;
    };

export enum UserReminderPreset {
  OnTime = "onTime",
  FiveMinutesBefore = "fiveMinutesBefore",
  TenMinutesBefore = "tenMinutesBefore",
  FifteenMinutesBefore = "fifteenMinutesBefore",
  ThirtyMinutesBefore = "thirtyMinutesBefore",
  OneHourBefore = "oneHourBefore",
  OneDayBefore = "oneDayBefore",
  SameDayInMorning = "sameDayInMorning",
  OneDayBeforeAtNight = "oneDayBeforeAtNight",
  OneDayBeforeInMorning = "oneDayBeforeInMorning",
  TwoDaysBeforeInMorning = "twoDaysBeforeInMorning",
  OneWeekBeforeInMorning = "oneWeekBeforeInMorning",
}

export const USER_REMINDERS_CONFIG: Record<UserReminderPreset, { value: IUserReminderValue }> = {
  [UserReminderPreset.OnTime]: {
    value: {
      constraint: "hasTime",
      add: {
        amount: 0,
        unit: "minutes",
      },
    },
  },
  [UserReminderPreset.FiveMinutesBefore]: {
    value: {
      constraint: "hasTime",
      add: {
        amount: -5,
        unit: "minutes",
      },
    },
  },
  [UserReminderPreset.TenMinutesBefore]: {
    value: {
      constraint: "hasTime",
      add: {
        amount: -10,
        unit: "minutes",
      },
    },
  },
  [UserReminderPreset.FifteenMinutesBefore]: {
    value: {
      constraint: "hasTime",
      add: {
        amount: -15,
        unit: "minutes",
      },
    },
  },
  [UserReminderPreset.ThirtyMinutesBefore]: {
    value: {
      constraint: "hasTime",
      add: {
        amount: -30,
        unit: "minutes",
      },
    },
  },
  [UserReminderPreset.OneHourBefore]: {
    value: {
      constraint: "hasTime",
      add: {
        amount: -1,
        unit: "hours",
      },
    },
  },
  [UserReminderPreset.OneDayBefore]: {
    value: {
      constraint: "hasTime",
      add: {
        amount: -1,
        unit: "days",
      },
    },
  },
  [UserReminderPreset.SameDayInMorning]: {
    value: {
      constraint: "fullDay",
      add: {
        amount: 0,
        unit: "days",
      },
      set: {
        hours: 9,
        minutes: 0,
      },
    },
  },
  [UserReminderPreset.OneDayBeforeAtNight]: {
    value: {
      constraint: "fullDay",
      add: {
        amount: -1,
        unit: "days",
      },
      set: {
        hours: 23,
        minutes: 50,
      },
    },
  },
  [UserReminderPreset.OneDayBeforeInMorning]: {
    value: {
      constraint: "fullDay",
      add: {
        amount: -1,
        unit: "days",
      },
      set: {
        hours: 9,
        minutes: 0,
      },
    },
  },
  [UserReminderPreset.TwoDaysBeforeInMorning]: {
    value: {
      constraint: "fullDay",
      add: {
        amount: -2,
        unit: "days",
      },
      set: {
        hours: 9,
        minutes: 0,
      },
    },
  },
  [UserReminderPreset.OneWeekBeforeInMorning]: {
    value: {
      constraint: "fullDay",
      add: {
        amount: -1,
        unit: "weeks",
      },
      set: {
        hours: 9,
        minutes: 0,
      },
    },
  },
};

export const USER_REMINDERS_BY_CONSTRAINT: Record<UserReminderConstraint, UserReminderPreset[]> = {
  fullDay: Object.entries(USER_REMINDERS_CONFIG)
    .filter(([_, item]) => item.value.constraint === "fullDay")
    .map(([key]) => key as UserReminderPreset),
  hasTime: Object.entries(USER_REMINDERS_CONFIG)
    .filter(([_, item]) => item.value.constraint === "hasTime")
    .map(([key]) => key as UserReminderPreset),
};

export const USER_REMINDER_UNITS = ["minutes", "hours", "days", "weeks"] as const;

export type UserReminderUnit = (typeof USER_REMINDER_UNITS)[number];

export interface IUserReminderValue {
  add: {
    amount: number;
    unit: UserReminderUnit;
  };
  set?: {
    hours: number;
    minutes: number;
  };
  constraint: UserReminderConstraint;
}

export type CardReminderSetting = {
  spaceId: string;
  reminderSettings: {
    reminderForDate: Omit<UserReminder, "id">;
    reminderForDatetime: Omit<UserReminder, "id">;
  } | null;
};
export const DEFAULT_VIEW_TYPES = ["myday", "allmytasks", "next7days", "mycalendar", "lastviewed"] as const;
export type DefaultViewType = (typeof DEFAULT_VIEW_TYPES)[number];

export enum BoardViewType {
  Kanban,
  Table,
  Calendar,
  Timeline,
}

export const BoardViewTypeAnalytics: Record<BoardViewType, string> = {
  [BoardViewType.Kanban]: "kanban",
  [BoardViewType.Table]: "table",
  [BoardViewType.Calendar]: "calendar",
  [BoardViewType.Timeline]: "timeline",
};

export const BOARD_VIEW_TYPES = [
  BoardViewType.Kanban,
  BoardViewType.Calendar,
  BoardViewType.Table,
  BoardViewType.Timeline,
] as const;

export enum WorkspaceType {
  Work = "WORK",
  Family = "FAMILY",
}
export type ActivityNotificationInterval = "immediate" | "daily" | "weekly";

export type ActivityNotificationConfig = {
  notificationInterval?: ActivityNotificationInterval;
  updateTypeConfig?: {
    card_tag_added?: {
      tagIds: string[];
    };
    card_moved_sections?: {
      sectionIds: string[];
    };
    card_created?: {};
    card_archived?: {};
    card_checked?: {};
    card_due?: {};
  };
};

export enum ShowCardCheckboxMode {
  Never,
  Inside,
  Everywhere,
}

export enum CustomFieldStatus {
  ACTIVE = 0,
  DELETED = 1,
}

export type CustomFieldDropdownOption = { id: string; name: string; color: string };
export type CustomFieldProperties = { dropdownOptions?: CustomFieldDropdownOption[] };

export enum CustomFieldType {
  Dropdown = "DROPDOWN",
  Number = "NUMBER",
  Progress = "PROGRESS",
  Currency = "CURRENCY",
  Rating = "RATING",
  Link = "LINK",
  Location = "LOCATION",
  Phone = "PHONE",
  Text = "TEXT",
  Date = "DATE",
  Email = "EMAIL",
  Duration = "DURATION",
}

export type CustomFieldValuePayloadsMap = {
  [CustomFieldType.Dropdown]: {
    dropdownOption: string;
  };
  [CustomFieldType.Number]: {
    number: number;
  };
  [CustomFieldType.Progress]: {
    progress: number;
  };
  [CustomFieldType.Currency]: {
    currency: string;
    amount: number;
  };
  [CustomFieldType.Rating]: {
    rating: number;
  };
  [CustomFieldType.Location]:
    | {
        displayName: string;
        locationId: string;
      }
    | { displayName: string };
  [CustomFieldType.Link]: {
    displayName: string;
    url: string;
  };
  [CustomFieldType.Phone]: {
    phone: string;
  };
  [CustomFieldType.Text]: {
    text: string;
  };
  [CustomFieldType.Duration]: {
    unit: "minutes" | "hours" | "days" | "weeks" | "months";
    amount: number;
  };
  [CustomFieldType.Date]: {
    date: string;
  };
  [CustomFieldType.Email]: {
    email: string;
  };
};

export type CustomFieldValuePayload = CustomFieldValuePayloadsMap[keyof CustomFieldValuePayloadsMap];
